import { unwrap } from "@sagot/api-client";
import { ADDON_MAX_UNITS, SEAT_ADDON_MAX, SEAT_ADDON_MIN } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Slider, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { useAddonPrice, useFormatters, type AddonKey, type BillingOverview } from "../shared";
import { heldUnits, taxOn, unitsLeft } from "./billing-model";
import { useHandleOrder } from "./checkout";
import { GatewayChooser, useGatewayChoice } from "./gateway-chooser";
import { TestModeNotice } from "./test-mode-notice";

interface AddonDialogProps {
  addon: Exclude<AddonKey, "white_label_mobile_setup">;
  overview: Pick<BillingOverview, "offered_gateways" | "region" | "currency" | "addons">;
  /** "Or upgrade to Pro · 15 seats included" (seats only). */
  upsell?: { label: string; onClick: () => void };
  /** Buying Secure launch Plus while Lite runs: Plus replaces Lite and the unused time is credited. */
  replacesLite?: boolean;
  onClose: () => void;
}

/** Add-ons whose quantity is a size: the slider shows "3 GB", not "3". */
const SIZED: ReadonlyMap<AddonKey, string> = new Map([["knowledge_storage_1gb", "GB"]]);
const SINGLE: ReadonlySet<AddonKey> = new Set(["remove_branding", "secure_launch_lite", "secure_launch"]);

/** Buy an add-on (extra seats / agents, message packs, branding removal, Secure launch) → checkout. */
export function AddonDialog({ addon, overview, upsell, replacesLite, onClose }: AddonDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const fmt = useFormatters();
  const message = useErrorMessage();
  const handleOrder = useHandleOrder();
  const [quantity, setQuantity] = useState(SEAT_ADDON_MIN);
  const held = heldUnits(overview.addons, addon);
  const left = unitsLeft(addon, held);
  const max = left ?? SEAT_ADDON_MAX;
  const unitSuffix = SIZED.get(addon);
  const choice = useGatewayChoice(overview.offered_gateways);
  const listed = useAddonPrice(addon);
  const currency = listed?.currency ?? overview.currency;
  const unit = Number(listed?.intervalPrice ?? 0);
  const dueNow = Number(listed?.dueNow ?? 0);
  const subtotal = dueNow * quantity;
  const tax = taxOn(subtotal, overview.region.tax);
  const money = (amount: number) => fmt.money(amount, currency);
  const buy = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/billing/addons", { body: { addon_key: addon, quantity, ...choice.body } })),
    onSuccess: (order) => {
      onClose();
      handleOrder(order, t(`billing.addons.bought_${addon}`, { count: quantity }));
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const per = listed?.per === "year" ? t("billing.perYear") : listed?.per === "month" ? t("billing.perMonth") : t("billing.addons.oneTime");
  const proration = listed?.proration ?? null;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(`billing.addons.title_${addon}`)}
        closeLabel={t("common.close")}
        className="max-w-md"
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button loading={buy.isPending} disabled={!listed || max < SEAT_ADDON_MIN} onClick={() => { buy.mutate(); }}>
              {t(`billing.addons.confirm_${addon}`)}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {unitSuffix ? <p className="text-sm text-fg-muted">{t(`billing.addons.body_${addon}`)}</p> : null}
          {SINGLE.has(addon) ? (
            <p className="text-sm text-fg-muted">{t(`billing.addons.body_${addon}`)}</p>
          ) : (
            <Slider
              label={t(`billing.addons.quantity_${addon}`)}
              value={Math.min(quantity, Math.max(max, SEAT_ADDON_MIN))}
              min={SEAT_ADDON_MIN}
              max={Math.max(max, SEAT_ADDON_MIN)}
              disabled={max <= SEAT_ADDON_MIN}
              {...(unitSuffix ? { formatValue: (value: number) => `${String(value)} ${unitSuffix}` } : {})}
              {...(left === null ? {} : { hint: t(left === 0 ? "billing.addons.capReached" : "billing.addons.capHint", { max: ADDON_MAX_UNITS[addon] ?? 0, held, left }) })}
              onValueChange={setQuantity}
            />
          )}
          <div className="space-y-1 rounded-md bg-surface-2 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-fg-muted">{t(`billing.addons.unit_${addon}`)}</span>
              <span className="font-semibold">
                {listed ? money(unit) : "—"} {per}
              </span>
            </div>
            {proration ? (
              <div className="flex justify-between gap-3">
                <span className="text-fg-muted">{t("billing.addons.dueNowDays", { days: proration.daysRemaining, total: proration.cycleDays })}</span>
                <span className="font-semibold tabular-nums">{money(dueNow)}</span>
              </div>
            ) : null}
            <div className="flex justify-between">
              <span className="text-fg-muted">{t("billing.addons.subtotal")}</span>
              <span className="font-semibold tabular-nums">{listed ? money(subtotal) : "—"}</span>
            </div>
            {tax > 0 ? (
              <div className="flex justify-between text-xs">
                <span className="text-fg-subtle">{overview.region.tax.label}</span>
                <span className="tabular-nums text-fg-muted">{money(tax)}</span>
              </div>
            ) : null}
            {upsell ? (
              <div className="flex justify-between pt-1">
                <span className="text-fg-muted">{t("billing.addons.orUpgrade")}</span>
                <button type="button" className="font-semibold text-primary-ink hover:underline" onClick={upsell.onClick}>
                  {upsell.label}
                </button>
              </div>
            ) : null}
          </div>
          {listed?.per ? <p className="text-xs text-fg-subtle">{t(`billing.addons.renews_${listed.per}${proration ? "_prorated" : ""}`, { price: money(unit), days: proration?.daysRemaining ?? 0 })}</p> : null}
          {replacesLite ? <p className="text-xs text-fg-muted" data-testid="addon-replaces-lite">{t("billing.addons.replacesLite")}</p> : null}
          <GatewayChooser state={choice} />
          <TestModeNotice gateways={[choice.gateway]} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
